import moment from "moment";
import { getProfitabilityComparison } from "@/api/financialReport";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";

// Módulo del tab Rentabilidad: SOLO el modo comparación de la card "Evolución
// de Ventas & Márgenes" (una línea por año para una métrica). El gráfico
// compuesto y la tabla del tab consumen `financialReports` (tableRowHeaders);
// separar la comparación acá evita que su ciclo de carga toque a la tabla y
// viceversa. Mismo shape/estado que la comparación de `salesReport`.

export const DEFAULT_COMPARISON_METRIC = "ventas";

const state = () => ({
    /**
     * @type {{
     *   months: number[],
     *   series: Array<{year: number, values: Array<number|null>}>,
     *   availableYears: number[],
     *   metric: string,
     *   unit: "money" | "percent",
     * } | null}
     */
    comparisonData: null,
    isComparisonLoading: false,
    comparisonError: null,
    // Años a comparar. Vacío hasta la primera apertura; el fetch lo inicializa
    // a [añoDelHeader, añoDelHeader-1].
    comparisonYears: [],
    // ventas | margen_producto | margen_bruto | margen_contribucion |
    // margen_ebitda | margen_neto (claves del backend).
    comparisonMetric: DEFAULT_COMPARISON_METRIC,
});

const mutations = {
    SET_COMPARISON_DATA(state, payload) { state.comparisonData = payload; },
    SET_COMPARISON_LOADING(state, isLoading) { state.isComparisonLoading = isLoading; },
    SET_COMPARISON_ERROR(state, error) { state.comparisonError = error; },
    SET_COMPARISON_YEARS(state, years) { state.comparisonYears = years; },
    SET_COMPARISON_METRIC(state, metric) { state.comparisonMetric = metric; },
};

const actions = {
    async fetchProfitabilityComparison({ state, commit, rootState }) {
        commit("SET_COMPARISON_LOADING", true);
        commit("SET_COMPARISON_ERROR", null);
        try {
            const endMonth = rootState.reportsFilters.endMonth;
            const endYear = moment(endMonth).year();
            let years = state.comparisonYears;
            if (!years || years.length === 0) {
                years = [endYear, endYear - 1];
                commit("SET_COMPARISON_YEARS", years);
            }
            const endDate = moment(endMonth).endOf("month").format("YYYY-MM-DD");
            const response = await getProfitabilityComparison(
                endDate, years, state.comparisonMetric
            );
            const {
                months,
                series,
                available_years: availableYears,
                metric,
                unit,
            } = response.data;
            commit("SET_COMPARISON_DATA", Object.freeze({
                months: Object.freeze(months || []),
                series: Object.freeze((series || []).map(Object.freeze)),
                availableYears: Object.freeze(availableYears || []),
                metric: metric || state.comparisonMetric,
                unit: unit || "money",
            }));
        } catch (error) {
            consoleHandlerError({ error, prefix: "[profitabilityReport]", silent: true });
            commit(
                "SET_COMPARISON_ERROR",
                extractErrorMessage(error, "Error al cargar la comparación.")
            );
        } finally {
            commit("SET_COMPARISON_LOADING", false);
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
};
